import { toast } from 'react-toastify';
import { getCookie, deleteCookie } from '../utils/utils';

const FALLBACK_MESSAGES = {
  400: 'Bad request',
  401: 'Session expired, please log in again',
  402: 'Payment required',
  403: 'You do not have permission to perform this action',
  404: 'Resource not found',
  413: 'Content too large',
  500: 'Something went wrong on the server',
};

export default function ApiInterceptor(axiosObject) {
  axiosObject.interceptors.request.use(
    (options) => {
      // The auth flow (stores/actions/authActions.js) writes the access
      // token to the `_aps_t` cookie via SetCookie. Earlier code here
      // read from localStorage.getItem('token'), which never existed —
      // so every authenticated request was sending `Bearer null` and
      // hitting auth:api → 401, even when the user was clearly logged in.
      //
      // We also have a legacy localStorage fallback because a small set
      // of pages may still be writing to it. Cookie is canonical.
      const token = getCookie('_aps_t') || localStorage.getItem('token');
      if (token) {
        options.headers['Authorization'] = 'Bearer ' + token;
      } else {
        // No token → don't send a malformed `Bearer null` header.
        // The server will reject any auth-protected route with 401
        // regardless; sending nothing is cleaner and lets public
        // endpoints work unauthenticated.
        delete options.headers['Authorization'];
      }
      return options;
    },
    (err) => {
      return Promise.reject(err);
    }
  );
  axiosObject.interceptors.response.use(
    (response) => {
      return response;
    },
    (err) => {
      // A request we intentionally aborted (e.g. a superseded home-screen
      // filter/search fetch) is not a failure — swallow it silently so no
      // "No server response" toast pops up.
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return Promise.reject(err);
      }

      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message;

      if (!err?.response) {
        toast.warn('No server response');
      } else {
        toast.warn(serverMessage || FALLBACK_MESSAGES[status] || 'Request failed');
      }

      if (status === 401) {
        // Auth has expired or was never valid. Clear BOTH storage
        // locations (cookie + localStorage) because the token lives in
        // the cookie but other ephemeral state might be in localStorage.
        // Without clearing the cookie we'd loop: reload → cookie still
        // set → interceptor sends stale token → 401 → reload → ...
        try { deleteCookie('_aps_t'); } catch {}
        try { deleteCookie('_aps_u'); } catch {}
        localStorage.clear();
        document.location.reload(true);
      }

      return Promise.reject(err);
    }
  );

  return axiosObject;
}
